import React, { useEffect, Component, ErrorInfo, ReactNode } from 'react'
import { RouterProvider } from 'react-router-dom'
import { ConfigProvider, Button, Result } from 'antd'
import type { Locale } from 'antd/es/locale'
import zhCN from 'antd/locale/zh_CN'
import zhTW from 'antd/locale/zh_TW'
import enUS from 'antd/locale/en_US'
import { I18nextProvider, useTranslation } from 'react-i18next'
import i18n from '@/locales'
import useI18nStore from '@/store/i18nStore'
import themeConfig from '@/theme/themeConfig'
import { router } from '@/router'
import { resolveBrandName, buildDocumentTitle } from '@/config/brand'

const antdLocaleMap: Record<string, Locale> = {
  'zh-CN': zhCN,
  'zh-TW': zhTW,
  'en-US': enUS,
}

// ── Error fallback UI (function component so hooks work) ─────────────────────
function ErrorFallback({ error }: { error: Error | null }) {
  const { t } = useTranslation()
  return (
    <div className="min-h-screen flex items-center justify-center">
      <Result
        status="error"
        title={t('error.boundary.title')}
        subTitle={error?.message || t('error.boundary.subTitle')}
        extra={
          <Button type="primary" onClick={() => window.location.reload()}>
            {t('error.boundary.reload')}
          </Button>
        }
      />
    </div>
  )
}

// ── Error boundary (class component required by React) ───────────────────────
interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

class ErrorBoundary extends Component<{ children: ReactNode }, ErrorBoundaryState> {
  constructor(props: { children: ReactNode }) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return <ErrorFallback error={this.state.error} />
    }
    return this.props.children
  }
}

function App() {
  const locale = useI18nStore((state) => state.locale)

  // 切换器已先加载好语言包再写 store，这里同步 i18n 当前语言；相同时跳过，免得多发一次
  // languageChanged 让全树重渲染
  useEffect(() => {
    if (i18n.language !== locale) void i18n.changeLanguage(locale)
  }, [locale])

  // 浏览器标签页标题：品牌名（env 优先，回退 i18n）+ 本地化后缀，随语言切换更新。
  // 语言包异步加载（#1224），store 的 locale 先于翻译就绪，须等 languageChanged 再取文案
  useEffect(() => {
    const updateTitle = () => {
      const name = resolveBrandName(i18n.t('common.appName'))
      document.title = buildDocumentTitle(name, i18n.t('common.appTitleSuffix'))
    }
    updateTitle()
    i18n.on('languageChanged', updateTitle)
    return () => i18n.off('languageChanged', updateTitle)
  }, [])

  return (
    <I18nextProvider i18n={i18n}>
      <ConfigProvider theme={themeConfig} locale={antdLocaleMap[locale] ?? zhCN}>
        <ErrorBoundary>
          <RouterProvider router={router} />
        </ErrorBoundary>
      </ConfigProvider>
    </I18nextProvider>
  )
}

export default App
